import React, { useEffect, useMemo, useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { garageSpecialRequestService } from '../../../../services/apiService';

const STATUS_OPTIONS = ['new', 'reviewing', 'quoted', 'approved', 'rejected', 'closed'];

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Quotes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const items = await garageSpecialRequestService.listMyQuoteRequests();
      setRows(items || []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const updateStatus = async (id, status) => {
    setSavingId(id);
    try {
      await garageSpecialRequestService.updateMyQuoteStatus(id, { status });
      setRows((prev) => prev.map((r) => (r._id === id ? { ...r, status } : r)));
    } catch (_) {
      // no-op
    } finally {
      setSavingId('');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Incoming Quote Requests</h2>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm sm:w-56"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
            <FaSpinner className="animate-spin" /> Loading requests...
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-gray-500">No quote requests yet.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Vehicle</th>
                <th className="px-4 py-3 text-left">Requested Services</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((item) => {
                const id = item._id;
                const vehicle = [item.vehicleDetails?.make, item.vehicleDetails?.model, item.vehicleDetails?.year]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <tr key={id}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold text-gray-900">{item.customerDetails?.name || '—'}</div>
                      <div className="text-xs text-gray-500">{item.customerDetails?.phone || '—'}</div>
                      <div className="text-xs text-gray-500">{item.customerDetails?.email || ''}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700">{vehicle || '—'}</td>
                    <td className="px-4 py-3 align-top text-gray-700">
                      {(item.requestedServices || []).length
                        ? item.requestedServices.join(', ')
                        : (item.clientGoals?.notes || '—')}
                    </td>
                    <td className="px-4 py-3 align-top text-gray-600">{formatDate(item.createdAt)}</td>
                    <td className="px-4 py-3 align-top">
                      <select
                        value={item.status || 'new'}
                        disabled={savingId === id}
                        onChange={(e) => updateStatus(id, e.target.value)}
                        className="w-36 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs font-semibold"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
